/**
 * El ticket de venta: lo que se esta armando ahora mismo en el mostrador.
 *
 * TRES DECISIONES QUE NO SON NEGOCIABLES
 * --------------------------------------
 * 1. EL TICKET SOBREVIVE A LA INTERRUPCION. Vive fuera del arbol de rutas y se
 *    persiste. La condicion normal de trabajo es que entre un WhatsApp o alguien
 *    con un equipo roto a mitad de una venta.
 *
 * 2. LA MONEDA DE UN PAGO LA IMPONE LA CAJA, NUNCA UN SELECTOR. Se elige
 *    "Efectivo (Caja mostrador)" o "Dolar billete (Caja fuerte)". El backend
 *    inserta con la moneda de la cuenta y su trigger rechaza otra cosa: ofrecer
 *    un switch permitiria contradecir a la caja, y de esa contradiccion solo
 *    sale plata mal registrada.
 *
 * 3. LO QUE SE TOCA A MANO SE VE. Un precio cambiado o un descuento aplicado
 *    quedan marcados en la fila, aunque el detalle este cerrado. Un descuento
 *    escondido es un descuento que nadie revisa.
 *
 * SOBRE LA DENSIDAD
 * -----------------
 * La fila muestra lo que se usa siempre —nombre, cantidad, importe— y esconde
 * detras de un toque lo que se usa a veces: precio, descuento, IMEI. Con los
 * seis campos a la vista, vender una funda se veria igual de complicado que
 * vender un iPhone, y la mayoria de las ventas son fundas.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Plus,
  Minus,
  Trash2,
  X,
  ChevronDown,
  Tag,
  Smartphone,
  ArrowLeftRight,
  UserPlus,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import {
  convertir,
  formatear,
  formatearCotizacion,
  parsearImporte,
  type Moneda,
} from "@tora/shared";
import type { EntregaUsado, MedioPago, PagoVentaContado } from "@/lib/tipos";
import { cn } from "@/lib/utils";
import { LogoCavado } from "@/components/Logo";
import { Aviso } from "@/components/ui/aviso";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Campo } from "@/components/ui/campo";
import { Card } from "@/components/ui/card";
import { Codigo } from "@/components/ui/codigo";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { Segmented } from "@/components/ui/segmented";
import { Importe } from "@/components/money/importe";
import { InputImporte } from "@/components/money/input-importe";
import { SelectorMoneda } from "@/components/money/switch-moneda";
import { EditorPagos, nuevoPago } from "@/components/cobro/EditorPagos";
import {
  cajaDelVuelto,
  chequeCompleto,
  esCheque,
  vueltoEnPesos,
  nombreCaja,
  proponerImporte,
  type CuentaFinanciera,
  type PagoCobro,
} from "@/components/cobro/logica";
import { BuscadorEnTicket } from "@/components/venta/BuscadorEnTicket";
import type { ProductoEncontrado } from "@/lib/usar-busqueda";
import { ClienteDelTicket } from "@/components/venta/SelectorCliente";
import { encargadosDe, useCobroEnMemoria } from "@/lib/ticket-store";
import { PasoDestinatario } from "@/components/venta/PasoDestinatario";
import { DialogUsado } from "@/components/usados/DialogUsado";
import { aEntrega } from "@/components/usados/logica";

// ─────────────────────────────────────────────────────────────────────────────
// Tipos
// ─────────────────────────────────────────────────────────────────────────────

export interface ItemTicket {
  producto_id: number;
  nombre: string;
  cantidad: number;
  precio_unitario: number;
  /** Moneda en la que el producto tiene su precio de lista. */
  precio_moneda: Moneda;
  /** Descuento de ESTA linea, aparte del descuento general del comprobante. */
  descuento_pct?: number;
  serie_imei?: string;
  /** true si alguien piso el precio de lista a mano. */
  precio_editado?: boolean;
  /**
   * El producto se vende a pedido: lo que exceda el stock deja un ENCARGO
   * hasta que llegue (0111). Antes el ticket no lo sabia, asi que la linea no
   * lo decia y se podia cobrar sin cliente: quedaba un encargo sin nadie a
   * quien avisarle cuando llegara. Cuantas se encargan lo dice `encargadosDe`.
   */
  a_pedido?: boolean;
  /** Lo que habia en stock al agregarlo: para avisar ANTES de vender de mas. */
  stock?: number;
  /** El color o la variante que pidio el cliente. Viaja como observaciones del renglon. */
  nota?: string;
  /**
   * Es un equipo usado concreto (bateria o IMEI): hay uno solo. El ticket dejaba
   * poner 2, decia «1 sale sin stock» y recien la base lo rechazaba al cobrar.
   */
  es_unidad?: boolean;
}

/** La caja es la misma que en el resto del cobro: una sola definicion, en `cobro/logica`. */
export type { CuentaFinanciera };

type TipoDescuento = "PORCENTAJE" | "MONTO";

const redondear = (v: number): number => Math.round(v * 100) / 100;

// ─────────────────────────────────────────────────────────────────────────────
// Campo numerico (porcentaje: no es plata)
// ─────────────────────────────────────────────────────────────────────────────

/** Confirma al salir o con Enter; descarta con Escape y vuelve al valor previo. */
function CampoNumero({
  valor,
  max,
  onConfirmar,
  etiqueta,
  id,
}: {
  valor: number;
  max?: number;
  onConfirmar: (v: number) => void;
  etiqueta: string;
  /** Lo inyecta `Campo` para enlazar la label. */
  id?: string;
}): JSX.Element {
  const [texto, setTexto] = useState(String(valor));
  useEffect(() => setTexto(String(valor)), [valor]);

  // Se marca mientras se tipea, no recien al salir: asi quien escribe "1.234,5x"
  // ve el borde rojo antes de que el campo vuelva solo al valor anterior.
  const parseado = parsearImporte(texto);
  const invalido = parseado === null || parseado < 0 || (max !== undefined && parseado > max);

  const confirmar = (): void => {
    // Un valor que no se entiende vuelve a lo que estaba. Convertirlo en 0
    // seria regalar mercaderia sin que nadie lo haya pedido.
    if (invalido || parseado === null) {
      setTexto(String(valor));
      return;
    }
    onConfirmar(parseado);
  };

  return (
    <Input
      id={id}
      value={texto}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={confirmar}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        if (e.key === "Escape") setTexto(String(valor));
      }}
      onFocus={(e) => e.target.select()}
      inputMode="decimal"
      aria-label={etiqueta}
      aria-invalid={invalido}
      title={invalido ? "No se entiende el número: al salir vuelve al valor anterior." : undefined}
      className="text-right tabular-nums"
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Fila
// ─────────────────────────────────────────────────────────────────────────────

function FilaItem({
  item,
  moneda,
  cotizacion,
  onCambiarCantidad,
  onEditar,
  onQuitar,
}: {
  item: ItemTicket;
  moneda: Moneda;
  cotizacion: number;
  onCambiarCantidad: (id: number, cantidad: number) => void;
  onEditar: (id: number, cambios: Partial<ItemTicket>) => void;
  onQuitar: (id: number) => void;
}): JSX.Element {
  const [abierto, setAbierto] = useState(false);
  const [cantidadTexto, setCantidadTexto] = useState(String(item.cantidad));

  useEffect(() => setCantidadTexto(String(item.cantidad)), [item.cantidad]);

  const unitario = convertir(item.precio_unitario, item.precio_moneda, moneda, cotizacion);
  const dto = item.descuento_pct ?? 0;
  const importe = redondear(unitario * item.cantidad * (1 - dto / 100));
  const tieneAjustes =
    dto > 0 || Boolean(item.precio_editado) || Boolean(item.serie_imei) || Boolean(item.nota);
  /**
   * Cuanto de esta linea sale sin stock. Vender sin stock esta permitido
   * (0106), pero el aviso llegaba recien en el panel de la venta cerrada: el
   * vendedor se enteraba despues de cobrar. Lo a pedido no cuenta: ya se sabe
   * que no esta y para eso es el encargo.
   */
  const sinStock =
    !item.a_pedido && item.stock !== undefined
      ? Math.max(item.cantidad - Math.max(item.stock, 0), 0)
      : 0;
  /** Un equipo usado es uno solo: no se vende de a dos (0092). */
  const equipoRepetido = Boolean(item.es_unidad) && item.cantidad > 1;
  /**
   * Cuanto de esta linea queda como encargo. Un producto a pedido con algo de
   * stock se entrega de lo que hay y encarga el resto: con stock 1 y cantidad
   * 2 el ticket decia «Hay 1: 1 sale sin stock», y la base dejaba un encargo.
   */
  const encargados = encargadosDe(item);

  const confirmarCantidad = useCallback(() => {
    // Este archivo ya usaba `parsearImporte` en otros cuatro lugares; la
    // cantidad habia quedado afuera. "1.500" es mil quinientas unidades.
    //
    // Y solo enteras: aca se venden unidades. `parsearImporte` acepta
    // decimales porque es para plata, y un "1,5" pasaba derecho al comprobante.
    const n = parsearImporte(cantidadTexto) ?? NaN;
    if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
      setCantidadTexto(String(item.cantidad));
      return;
    }
    // Un equipo usado es uno solo: «2» queda en 1.
    const cantidad = item.es_unidad ? Math.min(n, 1) : n;
    if (cantidad !== n) setCantidadTexto(String(cantidad));
    onCambiarCantidad(item.producto_id, cantidad);
  }, [cantidadTexto, item.cantidad, item.es_unidad, item.producto_id, onCambiarCantidad]);

  return (
    <div className={cn("px-3 py-2 sm:px-4", abierto && "bg-muted")}>
      {/* En el celular el nombre ocupa su propia linea y los controles bajan a
          la siguiente: con botones de 36px no entran los cinco en 343px. */}
      <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:gap-3">
        <div className="min-w-0 flex-1 basis-full sm:basis-0">
          <div className="truncate text-sm font-medium text-foreground">{item.nombre}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
            {/* La moneda de la linea la declara el selector del encabezado:
                todo el ticket esta en esa moneda, asi que el chip va solo en
                el precio de lista cuando esta en la otra. */}
            <span>
              <Importe monto={unitario} moneda={moneda} tamano="sm" codigo={false} /> c/u
            </span>
            {item.precio_moneda !== moneda && (
              <span>
                (lista{" "}
                <Importe monto={item.precio_unitario} moneda={item.precio_moneda} tamano="sm" />)
              </span>
            )}
            {/* Lo tocado a mano se ve tambien con el detalle cerrado. */}
            {dto > 0 && (
              <Badge variant="warning" className="tabular-nums">
                <Tag aria-hidden="true" />-{dto}%
              </Badge>
            )}
            {item.precio_editado && <Badge variant="warning">precio a mano</Badge>}
            {encargados > 0 && <Badge variant="info">a pedido</Badge>}
            {item.nota && <span className="truncate">{item.nota}</span>}
            {item.serie_imei && (
              <span className="inline-flex items-center gap-1">
                <Smartphone className="size-3" aria-hidden="true" />
                <Codigo>{item.serie_imei}</Codigo>
              </span>
            )}
            {encargados > 0 && item.stock !== undefined && item.stock > 0 && (
              <span className="text-info">
                {`Hay ${item.stock}: ${encargados} ${encargados === 1 ? "queda" : "quedan"} como encargo`}
              </span>
            )}
            {equipoRepetido ? (
              <span className="text-warning">Es un equipo: hay uno solo</span>
            ) : (
              sinStock > 0 && (
                <span className="text-warning">
                  {item.stock !== undefined && item.stock > 0
                    ? `Hay ${item.stock}: ${sinStock} ${sinStock === 1 ? "sale" : "salen"} sin stock`
                    : "Sin stock"}
                </span>
              )
            )}
          </div>
        </div>

        {/* La cantidad se puede TIPEAR, no solo clickear: vender seis fundas no
            puede costar seis clics. */}
        <div className="flex shrink-0 items-center gap-0.5">
          <IconButton
            icono={Minus}
            tamano="sm"
            variant="outline"
            onClick={() => onCambiarCantidad(item.producto_id, item.cantidad - 1)}
            aria-label={`Quitar uno de ${item.nombre}`}
          />
          <Input
            value={cantidadTexto}
            onChange={(e) => setCantidadTexto(e.target.value)}
            onBlur={confirmarCantidad}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
              if (e.key === "Escape") setCantidadTexto(String(item.cantidad));
            }}
            onFocus={(e) => e.target.select()}
            inputMode="decimal"
            aria-label={`Cantidad de ${item.nombre}`}
            className="h-9 w-12 px-1 py-0 text-center tabular-nums sm:h-8"
          />
          <IconButton
            icono={Plus}
            tamano="sm"
            variant="outline"
            onClick={() => onCambiarCantidad(item.producto_id, item.cantidad + 1)}
            // Un equipo usado es uno solo: no hay «otro» para agregar.
            disabled={Boolean(item.es_unidad) && item.cantidad >= 1}
            title={item.es_unidad ? "Es un equipo: hay uno solo" : undefined}
            aria-label={`Agregar uno de ${item.nombre}`}
          />
        </div>

        <Importe
          monto={importe}
          moneda={moneda}
          codigo={false}
          className="flex-1 justify-end font-semibold sm:w-28 sm:flex-none"
        />

        <IconButton
          icono={ChevronDown}
          tamano="sm"
          onClick={() => setAbierto((v) => !v)}
          aria-expanded={abierto}
          aria-label={`Precio, descuento e IMEI de ${item.nombre}`}
          className={cn(
            "shrink-0 [&_svg]:transition-transform",
            abierto && "[&_svg]:rotate-180",
            tieneAjustes || abierto ? "text-foreground" : "text-muted-foreground",
          )}
        />

        <IconButton
          icono={Trash2}
          tamano="sm"
          onClick={() => onQuitar(item.producto_id)}
          aria-label={`Quitar ${item.nombre}`}
          className="shrink-0 text-muted-foreground hover:text-destructive"
        />
      </div>

      {abierto && (
        <div className="mt-2 grid grid-cols-1 gap-4 border-t border-border pt-2 sm:grid-cols-3">
          {/*
            La moneda NO se elige aca: viene decidida del producto, desde que
            se lo cargo al catalogo. Ponerla tambien en la venta seria una
            perilla de mas que solo puede desincronizarse con el catalogo.
            Sin `onMonedaChange` el campo la deja fija y la muestra en el
            prefijo ($ / US$), que es lo que hace falta ver al editar.
            Un texto que no parsea emite `null` y no toca el precio.
          */}
          <InputImporte
            label="Precio unitario"
            valor={item.precio_unitario}
            moneda={item.precio_moneda}
            // Un negativo se marca en el campo: antes se descartaba en silencio
            // y el campo decia -5.000,00 mientras se cobraba el precio anterior.
            sinNegativos
            onValorChange={(v) => {
              if (v !== null && v >= 0)
                onEditar(item.producto_id, { precio_unitario: v, precio_editado: true });
            }}
          />

          <Campo label="Descuento %">
            <CampoNumero
              valor={dto}
              max={100}
              etiqueta={`Descuento de ${item.nombre}`}
              onConfirmar={(v) => onEditar(item.producto_id, { descuento_pct: v })}
            />
          </Campo>

          <Campo label="IMEI / N° de serie">
            <Input
              defaultValue={item.serie_imei ?? ""}
              onBlur={(e) =>
                onEditar(item.producto_id, { serie_imei: e.target.value.trim() || undefined })
              }
              placeholder="opcional"
              inputMode="numeric"
              aria-label={`IMEI de ${item.nombre}`}
              className="tabular-nums"
            />
          </Campo>

          {/* Lo que se encarga se encarga con su color: «IPHONE 16 128 GB» a
              secas no le dice al proveedor cual traer. Queda en el renglon de
              la venta y se lee en Por recibir. */}
          {encargados > 0 && (
            <Campo label="Color o variante que pidió" className="sm:col-span-3">
              <Input
                defaultValue={item.nota ?? ""}
                onBlur={(e) =>
                  onEditar(item.producto_id, { nota: e.target.value.trim() || undefined })
                }
                placeholder="Ej.: negro"
                aria-label={`Color o variante de ${item.nombre}`}
              />
            </Campo>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

/** «a, b y c», con la primera en mayuscula. */
function enumerar(partes: (string | null)[]): string {
  const p = partes.filter((x): x is string => x !== null);
  const texto = p.length > 1 ? `${p.slice(0, -1).join(", ")} y ${p[p.length - 1]}` : (p[0] ?? "");
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

/**
 * Lo que dice el boton de cobrar. Nombra la plata que se mueve.
 *
 * Con un equipo en parte de pago el valor de toma cuenta como pago pero no es
 * plata: con el equipo y el resto a cuenta, el boton decia «Cobrar US$ 50,00 y
 * dejar US$ 24,73 en cuenta» sin que entrara un peso a la caja, y el cajero
 * podia creer que tenia que recibir o registrar US$ 50 en efectivo.
 */
export function etiquetaCobrar(a: {
  aCuenta: boolean;
  /** Pago parcial que se puede confirmar (con cliente). */
  parcial: boolean;
  conEquipo: boolean;
  /** La plata que entra a las cajas, sin el equipo. */
  plata: number;
  /** La plata mas el valor de toma del equipo y el saldo a favor usado. */
  cobrado: number;
  diferencia: number;
  total: number;
  moneda: Moneda;
  /**
   * El saldo a favor que se usa, en la moneda de la venta (0121_8). Tampoco
   * es plata que entra: sin nombrarlo, el boton decia «Cobrar $ 30.000» y
   * entraban $ 20.000.
   */
  saldoAFavor?: number;
}): string {
  const f = (v: number) => formatear(v, a.moneda);
  const hayPlata = a.plata > 0.009;
  const saldo = a.saldoAFavor ?? 0;
  if (a.aCuenta) return `Cargar ${f(a.total)} a la cuenta`;
  // Lo que paga sin ser plata: el equipo y el saldo a favor. Se nombran los
  // dos. Antes, con equipo, el saldo quedaba afuera: «Tomar el equipo y
  // cerrar la venta» con $ 9.000 de saldo a favor usados, y el boton no decia
  // por que entraba menos plata.
  const sinPlata = [
    a.conEquipo ? "tomar el equipo" : null,
    saldo > 0.009 ? `usar ${f(saldo)} a favor` : null,
  ].filter((p): p is string => p !== null);
  if (a.parcial) {
    // Se nombran los numeros: lo que entra a la caja y lo que queda
    // debiendo. Es una decision con consecuencias y quien vende tiene que
    // verla antes de apretar, no despues.
    if (sinPlata.length === 0) return `Cobrar ${f(a.cobrado)} y dejar ${f(a.diferencia)} en cuenta`;
    return enumerar([
      hayPlata ? `cobrar ${f(a.plata)}` : null,
      ...sinPlata,
      `dejar ${f(a.diferencia)} en cuenta`,
    ]);
  }
  if (sinPlata.length > 0) {
    // Lo que el equipo y el saldo no cubren es la plata que tiene que entrar.
    // Antes se miraba si habia plata cargada: con el equipo solo y un
    // faltante sin cliente decia «Tomar el equipo y cerrar la venta» mientras
    // el aviso decia «Falta cobrar», y con plata y equipo decia «Cobrar» el
    // total entero aunque entraba solo una parte.
    const resto = a.total - (a.cobrado - a.plata);
    return resto > 0.009
      ? enumerar([`cobrar ${f(resto)}`, ...sinPlata])
      : enumerar([...sinPlata, "cerrar la venta"]);
  }
  return `Cobrar ${f(a.total)}`;
}

export interface TicketVentaProps {
  items: ItemTicket[];
  moneda: Moneda;
  cotizacion: number;
  cuentas: CuentaFinanciera[];
  clienteId?: number | null;
  clienteNombre?: string | null;
  /**
   * La moneda de la cuenta corriente del cliente, si ya se sabe. Con ella el
   * ticket avisa cuando la deuda va a quedar en otra moneda que la de la venta.
   */
  clienteAnclaje?: Moneda | null;
  /**
   * El saldo a favor que el cliente puede usar, en la moneda de su cuenta
   * (`clienteAnclaje`). El ticket lo OFRECE como medio de pago; quien vende
   * decide si lo usa y cuanto, hasta el total (0121_8). Antes no habia forma
   * de usarlo: la venta quedaba impaga y el remito decia que debia.
   */
  saldoAFavor?: number | null;
  guardando?: boolean;
  mostrarInternos?: boolean;
  descuentoTipo: TipoDescuento | null;
  descuentoValor: number;
  aCuenta: boolean;
  onCambiarACuenta: (v: boolean) => void;
  /** `textoInicial` viaja cuando se abrio tipeando desde el paso previo. */
  onElegirCliente: (textoInicial?: string) => void;
  /** La venta se declaro de mostrador en el paso previo. */
  mostrador: boolean;
  /** Contestar "mostrador" y pasar a cargar productos. */
  onElegirMostrador: () => void;
  onQuitarCliente: () => void;
  onCambiarCantidad: (productoId: number, cantidad: number) => void;
  onEditarItem: (productoId: number, cambios: Partial<ItemTicket>) => void;
  onQuitar: (productoId: number) => void;
  onCambiarMoneda: (m: Moneda) => void;
  onCambiarDescuento: (tipo: TipoDescuento | null, valor: number) => void;
  onAgregarProducto: (p: ProductoEncontrado) => void;
  onCobrar: (
    pagos: PagoVentaContado[],
    entrega: EntregaUsado | null,
    /** El cliente no quiso el vuelto: queda en la caja como propina. */
    vueltoPropina: boolean,
    /**
     * Lo que la venta deja debiendo en la cuenta, en la moneda de la venta: el
     * total a cuenta, la diferencia en un pago parcial, 0 si se paga entera.
     * Pagada entera no le importa el limite de credito del cliente, y la
     * pantalla se lo dice a la base con `forzar`. Va el monto y no un «deja
     * deuda» a secas porque la base compara el TOTAL de la venta contra el
     * limite, y un pago parcial que deja $ 1.000 de deuda preguntaba por los
     * $ 14.000 de la venta.
     */
    deuda: number,
    /** Cuanto del saldo a favor paga esta venta, en la moneda de la venta. */
    saldoAFavor: number,
  ) => void;
  onVaciar: () => void;
  /**
   * Que los pagos cargados sobrevivan a ir a otra pantalla y volver (solo en
   * memoria, no a un reload). Lo pide el mostrador; suelto, cada ticket
   * arranca sin pagos.
   */
  recordarCobro?: boolean;
}

export function TicketVenta({
  items,
  moneda,
  cotizacion,
  cuentas,
  clienteId = null,
  clienteNombre,
  clienteAnclaje = null,
  saldoAFavor = null,
  guardando = false,
  mostrarInternos = true,
  descuentoTipo,
  descuentoValor,
  aCuenta,
  onCambiarACuenta,
  onElegirCliente,
  mostrador,
  onElegirMostrador,
  onQuitarCliente,
  onCambiarCantidad,
  onEditarItem,
  onQuitar,
  onCambiarMoneda,
  onCambiarDescuento,
  onAgregarProducto,
  onCobrar,
  onVaciar,
  recordarCobro = false,
}: TicketVentaProps): JSX.Element {
  // Los pagos son los del editor comun de cobro; `medio` viaja como string y se
  // tipa como `MedioPago` recien al cobrar, igual que en la seña y la entrega.
  //
  // Con `recordarCobro` (el mostrador) sobreviven a ir a otra pantalla y
  // volver: antes el ticket quedaba y el pago cargado se perdia.
  const [pagos, setPagos] = useCobroEnMemoria<PagoCobro[]>("pagos", [], recordarCobro);
  const [mostrarDto, setMostrarDto] = useState(false);
  /*
    VENDER EN DOS PASOS, EN EL CELULAR
    ----------------------------------
    Primero el ticket (los productos enteros, el descuento y el total) y,
    con «Siguiente», una pantalla solo para cómo paga. Antes iba todo junto:
    el ticket era una ventana de 128 px donde del segundo producto se veía el
    nombre y el dedo se trababa, y «Cobrar» quedaba debajo del teclado. En la
    compu se ve todo junto, como siempre: `paso` solo esconde con `max-md:`.
  */
  const [paso, setPaso] = useState<"ticket" | "cobro">("ticket");
  // Mientras se busca un producto, en el celular se esconde lo de arriba
  // (Reiniciar, la moneda y el destinatario): los resultados quedan pegados
  // al campo y entran más arriba del teclado.
  const [buscando, setBuscando] = useState(false);
  // Un ticket vacío (recién cobrado o reiniciado) vuelve al primer paso.
  if (paso === "cobro" && items.length === 0) setPaso("ticket");
  const tipoDto: TipoDescuento = descuentoTipo ?? "PORCENTAJE";
  // El texto crudo del descuento en porcentaje, para poder marcar "no se
  // entiende" sin convertirlo en 0. Se resincroniza cuando el valor cambia
  // desde afuera.
  const [descuentoTexto, setDescuentoTexto] = useState(
    descuentoValor ? String(descuentoValor) : "",
  );
  useEffect(() => {
    setDescuentoTexto((t) => {
      const actual = parsearImporte(t);
      return actual === descuentoValor || (t.trim() === "" && descuentoValor === 0)
        ? t
        : descuentoValor
          ? String(descuentoValor)
          : "";
    });
  }, [descuentoValor]);
  const descuentoInvalido =
    descuentoTexto.trim() !== "" && (parsearImporte(descuentoTexto) ?? -1) < 0;
  // El equipo que el cliente entrega en parte de pago. Como los pagos, no se
  // persiste: es del momento del cobro.
  const [entrega, setEntrega] = useCobroEnMemoria<EntregaUsado | null>(
    "entrega",
    null,
    recordarCobro,
  );
  const [dialogEntrega, setDialogEntrega] = useState(false);
  // "Quedate con el vuelto". Se destilda solo cuando deja de haber vuelto.
  const [vueltoPropina, setVueltoPropina] = useCobroEnMemoria(
    "vueltoPropina",
    false,
    recordarCobro,
  );
  /**
   * Cuanto del saldo a favor se eligio usar, en la moneda de la venta. `null`
   * es que no se usa: nunca se aplica solo.
   */
  const [saldoElegido, setSaldoElegido] = useCobroEnMemoria<number | null>(
    "saldoAFavor",
    null,
    recordarCobro,
  );
  // Otro cliente, otro saldo: lo elegido para el anterior no se arrastra.
  const [clienteDelSaldo, setClienteDelSaldo] = useState(clienteId);
  if (clienteDelSaldo !== clienteId) {
    setClienteDelSaldo(clienteId);
    setSaldoElegido(null);
  }

  const subtotal = useMemo(
    () =>
      redondear(
        items.reduce((acc, it) => {
          const u = convertir(it.precio_unitario, it.precio_moneda, moneda, cotizacion);
          return acc + u * it.cantidad * (1 - (it.descuento_pct ?? 0) / 100);
        }, 0),
      ),
    [items, moneda, cotizacion],
  );

  const descuento = useMemo(() => {
    if (!descuentoTipo || descuentoValor <= 0) return 0;
    const bruto =
      descuentoTipo === "PORCENTAJE" ? (subtotal * descuentoValor) / 100 : descuentoValor;
    // Nunca deja el total en negativo: un descuento mayor al subtotal es un
    // error de carga, no una nota de credito.
    return redondear(Math.min(Math.max(bruto, 0), subtotal));
  }, [subtotal, descuentoTipo, descuentoValor]);

  const total = redondear(subtotal - descuento);

  /** El saldo a favor del cliente, pasado a la moneda de la venta. */
  const saldoDisponible =
    clienteId !== null && clienteAnclaje !== null && (saldoAFavor ?? 0) > 0.009
      ? convertir(saldoAFavor ?? 0, clienteAnclaje, moneda, cotizacion)
      : 0;
  /** Hasta el total: el saldo a favor no se devuelve en plata. */
  const topeSaldo = redondear(Math.max(Math.min(total, saldoDisponible), 0));
  const saldoUsado =
    !aCuenta && saldoElegido !== null && topeSaldo > 0.009
      ? redondear(Math.min(Math.max(saldoElegido, 0), topeSaldo))
      : 0;

  /** La plata que entra a las cajas, sin el equipo. */
  const plata = useMemo(
    () =>
      pagos.reduce((acc, p) => {
        const cuenta = cuentas.find((c) => c.id === p.cuenta_id);
        return cuenta && p.importe !== null
          ? acc + convertir(p.importe, cuenta.moneda, moneda, cotizacion)
          : acc;
      }, 0),
    [pagos, cuentas, moneda, cotizacion],
  );
  /** El equipo entregado, en la moneda de la venta: vale como pago, aunque no sea plata. */
  const toma = useMemo(
    () =>
      entrega ? convertir(entrega.valor_toma, entrega.moneda ?? moneda, moneda, cotizacion) : 0,
    [entrega, moneda, cotizacion],
  );
  // El saldo a favor tambien paga, aunque no entre a ninguna caja.
  const cobrado = plata + toma + saldoUsado;
  /**
   * Lo que tiene que entrar en plata, que es lo que proponen las filas de
   * «¿Cómo paga?»: el total sin el saldo a favor y sin el equipo. Sin restar
   * la toma, la fila proponia el total entero, aparecia un vuelto por lo que
   * valia el equipo y el boton decia otro importe.
   */
  const objetivoPlata = (tomaDelEquipo: number): number =>
    redondear(Math.max(total - saldoUsado - tomaDelEquipo, 0));

  /**
   * Paso 1 → paso 2 en el celular. Si todavía no hay medio de pago, se carga
   * el de siempre por lo que falta (lo mismo que tocar «¿Cómo paga?»): sin
   * esto, cada venta pedía un toque más y «Cobrar» quedaba gris hasta darlo.
   */
  const irACobrar = (): void => {
    if (!aCuenta && pagos.length === 0) {
      const p = nuevoPago(cuentas, [], objetivoPlata(toma), moneda, cotizacion);
      if (p) setPagos([p]);
    }
    setPaso("cobro");
  };

  /**
   * Items cuyo precio de LISTA esta en otra moneda que la de facturacion.
   *
   * Es el caso normal del local: se lleva un iPhone cotizado en dolares y un
   * cargador cotizado en pesos, en la misma venta. El comprobante tiene UNA
   * moneda —asi lo exige el modelo y asi debe ser: un comprobante con dos
   * monedas adentro no se puede imputar ni cobrar—, y los items que estan en la
   * otra se convierten a la cotizacion de la venta.
   *
   * Eso funciona solo, pero tiene que VERSE: la conversion congela ese precio a
   * la cotizacion de hoy, y quien vende tiene que saber que eso paso.
   */
  const convertidos = useMemo(
    () => items.filter((i) => i.precio_moneda !== moneda),
    [items, moneda],
  );

  /**
   * El total desglosado por la moneda de LISTA de cada item.
   *
   * La mayoria del catalogo se cotiza en pesos y algunos equipos en dolares. Un
   * total unico esconde de donde salio cada parte, y quien vende necesita saber
   * cuanto de esa venta esta atado al dolar: es la parte que cambia de precio
   * mañana.
   *
   * Se calcula sobre el precio de lista, ANTES de convertir, para que los
   * numeros del desglose sean los que figuran en el catalogo.
   */
  const desglose = useMemo(() => {
    const acc = { ARS: 0, USD: 0 };
    for (const i of items) {
      const dto = 1 - (i.descuento_pct ?? 0) / 100;
      acc[i.precio_moneda] += i.precio_unitario * i.cantidad * dto;
    }
    return { ARS: redondear(acc.ARS), USD: redondear(acc.USD) };
  }, [items]);

  const mixto = desglose.ARS > 0 && desglose.USD > 0;

  /** El total SIEMPRE en pesos: es la moneda en la que se cobra casi todo. */
  const totalArs = convertir(total, moneda, "ARS", cotizacion);

  /**
   * Cobrar en otra moneda tiene un grano: US$ 42,45 son $ 67.495,50 y US$ 42,46
   * ya se pasan de $ 67.500. Sin tolerar ese medio centavo convertido, cobrar
   * todo en dolares dejaba «Queda debiendo $ 4,50» y Cobrar apagado. Es el
   * mismo grano que acepta `emitir_venta_contado`: MEDIO centavo de dolar, lo
   * que queda con el importe mas cercano (0121_8). Hasta ahi la deuda se da
   * por cerrada; de ahi para arriba es plata que falta.
   */
  const grano = useMemo(
    () =>
      pagos.reduce((g, p) => {
        const cuenta = cuentas.find((c) => c.id === p.cuenta_id);
        return cuenta && cuenta.moneda !== moneda
          ? Math.max(g, convertir(0.005, cuenta.moneda, moneda, cotizacion))
          : g;
      }, 0),
    [pagos, cuentas, moneda, cotizacion],
  );

  const diferencia = redondear(total - cobrado);
  const falta = diferencia > grano + 0.009;
  /**
   * El vuelto con el mismo umbral que `emitir_venta_contado`: mas de un grano
   * (y nunca menos de un centavo). Con `< -0,009` una venta de $ 14.000 pagada
   * con los US$ 8,81 propuestos anunciaba «Vuelto $ 7,90» que el servidor no
   * asentaba: si el cajero los entregaba, el cajon quedaba corto.
   */
  const vuelto = diferencia < -(Math.max(grano, 0.01) + 0.009);
  /**
   * Lo pagado de mas es vuelto, o propina, tenga o no cliente (0145): nunca
   * queda como saldo a favor.
   */
  const ofrecePropina = vuelto && !aCuenta;

  /**
   * Pago parcial: entra plata, pero no toda. Es el caso mas comun del
   * mostrador —se lleva el equipo, deja una sena— y la diferencia queda como
   * deuda en la cuenta corriente del cliente.
   *
   * Se exige que haya entrado ALGO: unos medios de pago cargados en cero no
   * son un pago parcial, son un formulario a medio llenar. Para no cobrar nada
   * esta el interruptor "A la cuenta".
   */
  const parcial =
    falta && (pagos.length > 0 || entrega !== null || saldoUsado > 0.009) && cobrado > 0.009;

  /**
   * Una deuda necesita un titular. Sin cliente no hay cuenta corriente donde
   * anotarla ni a quien reclamarle: seria plata perdida sin rastro. El backend
   * tambien lo rechaza (`PAGO_PARCIAL_SIN_CLIENTE`), pero avisar aca evita que
   * la persona cargue toda la venta para chocarse al final.
   */
  const parcialSinCliente = parcial && clienteId === null;

  /**
   * Lo a pedido deja un encargo, y un encargo necesita a quien avisarle cuando
   * llega. Sin cliente quedaba en Por recibir con «—» y nadie sabia de quien
   * era. Misma salida que la deuda sin titular: asignar o crear uno ahi mismo.
   */
  const hayAPedido = items.some((i) => encargadosDe(i) > 0);
  const encargoSinCliente = hayAPedido && clienteId === null;

  /**
   * Un cheque puede rebotar, y entonces alguien tiene que quedar debiendo. Sin
   * cliente, el rechazo sacaba la plata de la caja pero la venta seguia
   * cobrada. El servidor tambien lo frena; aca se avisa antes de cobrar.
   */
  const chequeSinCliente =
    !aCuenta &&
    clienteId === null &&
    pagos.some((p) => p.medio === "CHEQUE" || p.medio === "ECHEQ");

  /**
   * Un total de cero (precio en 0 o descuento del 100%) no es una venta que
   * se pueda cobrar: el pago propuesto es $ 0, se descarta, y el servidor
   * rebotaba con «necesita al menos un medio de pago», que no dice que paso.
   */
  const totalCero = items.length > 0 && total <= 0.009;

  /**
   * La deuda en la moneda de la cuenta, cuando no es la de la venta.
   *
   * Una venta en dolares a cuenta de un cliente con la cuenta fija en pesos
   * deja la deuda congelada en pesos al dolar de hoy: es el diseño de esas
   * cuentas. El boton decia «Cargar US$ 700 a la cuenta» y nada mas, y despues
   * la ficha decia $ 1.109.500 fijos. Se dice aca, antes de cobrar.
   */
  const deudaEnSuCuenta =
    clienteId !== null && clienteAnclaje !== null && clienteAnclaje !== moneda
      ? aCuenta
        ? convertir(total, moneda, clienteAnclaje, cotizacion)
        : parcial
          ? convertir(diferencia, moneda, clienteAnclaje, cotizacion)
          : null
      : null;

  // Un pago que no se entiende (`importe: null`) no se descarta: frena el cobro.
  const hayPagoInvalido = !aCuenta && pagos.some((p) => p.importe === null);
  // Un cheque sin numero, banco, librador o fecha no puede entrar a cartera, y
  // la base lo rechaza: se frena aca, mientras se escribe, como en el cobro.
  const chequeIncompleto = !aCuenta && pagos.some((p) => !chequeCompleto(p));

  /**
   * Hay vuelto en billetes y no hay cajon en pesos de donde darlo. La base lo
   * frena (0121_1); aca se avisa antes de apretar Cobrar.
   */
  const vueltoSinCajon = vuelto && !aCuenta && cajaDelVuelto(pagos, cuentas) === null;

  const puedeCobrar =
    items.length > 0 &&
    !guardando &&
    !hayPagoInvalido &&
    !chequeIncompleto &&
    !vueltoSinCajon &&
    !encargoSinCliente &&
    !chequeSinCliente &&
    !totalCero &&
    (aCuenta ||
      ((pagos.length > 0 || entrega !== null || saldoUsado > 0.009) &&
        (!falta || (parcial && !parcialSinCliente))));

  const cobrar = useCallback(() => {
    onCobrar(
      aCuenta
        ? []
        : pagos
            .filter(
              (p): p is PagoCobro & { importe: number } => p.importe !== null && p.importe > 0,
            )
            .map((p) => ({
              cuenta_id: p.cuenta_id,
              medio: p.medio as MedioPago,
              importe: p.importe,
              // El cheque viaja con el pago, como en el cobro de cuenta
              // corriente. Antes se tiraba aca: la pantalla pedia los datos
              // del papel y el cheque nunca entraba a cartera.
              ...(esCheque(p.medio) && p.cheque
                ? { cheque: { ...p.cheque, tipo: p.medio as "CHEQUE" | "ECHEQ" } }
                : {}),
            })),
      aCuenta ? null : entrega,
      ofrecePropina && vueltoPropina,
      aCuenta ? total : parcial ? diferencia : 0,
      aCuenta ? 0 : saldoUsado,
    );
    // Los pagos NO se limpian aca: si el servidor rechaza o se corta la red,
    // borrarlos obligaba a cargar de nuevo todos los medios. Se limpian cuando
    // el ticket queda vacio, que es lo que pasa cuando la venta salio.
  }, [
    pagos,
    onCobrar,
    aCuenta,
    entrega,
    ofrecePropina,
    vueltoPropina,
    parcial,
    total,
    diferencia,
    saldoUsado,
  ]);

  // Se ajusta en el render y no en un efecto: es estado que depende de una
  // prop, y asi no hay un cuadro con los pagos viejos al lado del ticket vacio.
  // Arranca en `false` para que un ticket que se monta vacio tambien limpie los
  // pagos que hubieran quedado en memoria (`recordarCobro`).
  const [estabaVacio, setEstabaVacio] = useState(false);
  if ((items.length === 0) !== estabaVacio) {
    setEstabaVacio(items.length === 0);
    if (items.length === 0) {
      setPagos([]);
      setEntrega(null);
      setVueltoPropina(false);
      setSaldoElegido(null);
    }
  }

  /**
   * De que caja sale el vuelto, con la misma regla que asienta
   * `emitir_venta_contado` (0121_1): siempre el cajon de Efectivo en pesos,
   * ver `cajaDelVuelto`. Antes una venta en pesos pagada con dolares decia
   * «Sale de Efectivo (US$): US$ 4,34» y el cajero daba pesos.
   */
  const cajaVuelto = useMemo(() => cajaDelVuelto(pagos, cuentas), [pagos, cuentas]);

  /**
   * Todavia nadie contesto a quien se le vende. Ver `PasoDestinatario`: el
   * `items.length === 0` esta para no trabar tickets guardados por la version
   * anterior, que no tenian el campo.
   */
  const sinDestinatario = items.length === 0 && clienteId === null && !mostrador;

  /**
   * Reiniciar pide confirmacion en dos pasos, no dialogo.
   *
   * El boton esta SIEMPRE visible, que es lo que se pedia — y eso lo vuelve
   * facil de apretar sin querer. Perder un ticket de ocho renglones por un
   * clic al pasar es de las cosas que mas enojan de un sistema de mostrador.
   * Un dialogo modal para esto seria desproporcionado y ademas se contesta en
   * automatico; que el propio boton pregunte obliga a mirar lo que se aprieta.
   *
   * Con el ticket vacio no pregunta nada: no hay trabajo que perder.
   */
  const [confirmandoReinicio, setConfirmandoReinicio] = useState(false);

  useEffect(() => {
    if (!confirmandoReinicio) return;
    // Si se arrepintio y siguio en otra cosa, el boton vuelve solo a su estado
    // normal. Un "¿seguro?" que queda colgado para siempre se termina apretando
    // por inercia la proxima vez.
    const t = setTimeout(() => setConfirmandoReinicio(false), 4000);
    return () => clearTimeout(t);
  }, [confirmandoReinicio]);

  const reiniciar = useCallback(() => {
    if (items.length === 0) {
      onVaciar();
      return;
    }
    if (!confirmandoReinicio) {
      setConfirmandoReinicio(true);
      return;
    }
    onVaciar();
    setConfirmandoReinicio(false);
  }, [items.length, confirmandoReinicio, onVaciar]);

  return (
    // En el celular no tiene alto fijo: crece con lo que tiene y se desliza la
    // pantalla entera, sin zonas que se deslizan adentro de otras.
    <div className="flex flex-col md:h-full">
      {/* ── Encabezado ── El titulo de la pantalla lo pone el PageHeader de la
          ruta; aca quedan solo los dos controles del ticket. */}
      <div
        className={cn(
          "flex items-center justify-between gap-2 border-b border-border px-4 py-3",
          buscando && "max-md:hidden",
        )}
      >
        {/*
          Empezar de nuevo, visible en todos los estados del ticket: con la
          compuerta preguntando, con el destinatario elegido y sin productos,
          y con la venta armada. Antes solo existia "Vaciar" en el pie, que
          aparecia recien con el primer item — justo despues del momento en
          que mas se necesita, que es cuando elegiste mal el cliente y todavia
          no cargaste nada.
        */}
        <Button
          variant={confirmandoReinicio ? "destructive" : "ghost"}
          size="sm"
          onClick={reiniciar}
          disabled={sinDestinatario || guardando}
          aria-label={confirmandoReinicio ? "Confirmar: reiniciar la venta" : "Reiniciar la venta"}
          className={cn(!confirmandoReinicio && "text-muted-foreground")}
        >
          <RotateCcw aria-hidden="true" />
          {confirmandoReinicio ? "¿Seguro?" : "Reiniciar"}
        </Button>

        {/* Moneda de FACTURACION: en cual queda emitido el comprobante. No
            confundir con la moneda de un pago, que la impone la caja. */}
        <SelectorMoneda
          valor={moneda}
          onChange={onCambiarMoneda}
          tamano="compacto"
          etiqueta="Moneda de facturación"
        />
      </div>

      {/*
        El destinatario ocupa su propia banda, no un rincon del encabezado.
        Mientras la compuerta esta preguntando no se dibuja: seria la misma
        pregunta dos veces en la misma pantalla, y la de abajo es la que manda.
      */}
      {!sinDestinatario && (
        <div className={cn("border-b border-border px-3 py-2", buscando && "max-md:hidden")}>
          <ClienteDelTicket
            clienteId={clienteId}
            nombre={clienteNombre ?? null}
            mostrador={mostrador}
            cotizacion={cotizacion}
            // `onCambiar` termina en un onClick: pasar `onElegirCliente` a
            // secas colaria el evento del mouse como `textoInicial`.
            onCambiar={() => onElegirCliente()}
            onQuitar={onQuitarCliente}
          />
        </div>
      )}

      {/*
        Mientras no se sepa a quien se le vende, no hay campo de productos. La
        pregunta va primero y ocupa el lugar de la carga, no un cartel al lado
        que se pueda saltear.

        La condicion mira `items.length === 0` ademas del destinatario a
        proposito: un ticket a medio cargar guardado por la version anterior
        —que no tenia el campo `mostrador`— no puede quedar trabado detras de
        una pregunta que en su momento nadie hizo. Si ya hay productos, la
        venta sigue por donde iba y el destinatario se toca desde el chip.
      */}
      {sinDestinatario ? (
        <PasoDestinatario onMostrador={onElegirMostrador} onCliente={onElegirCliente} />
      ) : (
        <>
          {/* El campo de carga es la accion principal de la pantalla, y esta
              destacado como tal mientras el ticket esta vacio. */}
          <div className={cn(paso === "cobro" && "max-md:hidden")}>
            <BuscadorEnTicket
              moneda={moneda}
              mostrarInternos={mostrarInternos}
              onAgregar={onAgregarProducto}
              autoFocus={items.length === 0}
              destacado={items.length === 0}
              onBuscandoChange={setBuscando}
            />
          </div>

          {/* ── Items ── La lista tiene un piso: el bloque de cobro se comia
              todo el alto y de los items asomaba medio renglon. Lo que no
              entra lo scrollea el bloque de cobro, tambien en desktop: con
              cliente, varios pagos, un equipo en parte de pago y el aviso de
              saldo, el cobro no entra en la tarjeta y la tarjeta corta. */}
          <div
            className={cn(
              "md:min-h-32 md:flex-1 md:overflow-y-auto",
              paso === "cobro" && "max-md:hidden",
            )}
          >
            {items.length === 0 ? (
              /*
                El vacio va sobre `bg-muted`, no sobre el blanco de la tarjeta.
                Es el mismo token que ya usan las cabeceras de tabla, asi que
                no inventa un gris nuevo: en claro baja de 100% a 96% de
                luminancia y en oscuro SUBE de 12% a 17%. En los dos casos se
                despega de la tarjeta, que es lo unico que se le pide.

                El gris se pinta aca y no en el contenedor del scroll a
                proposito: las filas del ticket no tienen fondo propio —se
                separan con `divide-y`— asi que pintar el contenedor les
                cambiaria el fondo a todas. El gris dice "esta bandeja esta
                vacia"; en cuanto entra el primer producto el contenido pasa a
                ser la lista y el gris ya no tiene nada que marcar.

                Adentro va la manzana cavada y nada mas. El texto de ayuda
                («Escribí arriba el nombre o el código del producto») repetia
                lo que ya dice el campo, que llega enfocado y con su propio
                placeholder. El logo mide 8rem y, con la tarjeta baja, se achica
                al alto que le deja la bandeja.
              */
              <div className="flex h-full items-center justify-center bg-muted p-6">
                <LogoCavado className="h-32 max-h-full" />
              </div>
            ) : (
              <div className="divide-y divide-border">
                {items.map((it) => (
                  <FilaItem
                    key={it.producto_id}
                    item={it}
                    moneda={moneda}
                    cotizacion={cotizacion}
                    onCambiarCantidad={onCambiarCantidad}
                    onEditar={onEditarItem}
                    onQuitar={onQuitar}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ── Total y cobro ── */}
          {items.length > 0 && (
            <div className="border-t border-border md:min-h-0 md:overflow-y-auto">
              <div className={cn("px-4 pt-3", paso === "cobro" && "max-md:hidden")}>
                {(descuento > 0 || mostrarDto) && (
                  <div className="mb-2 space-y-2">
                    <div className="flex items-baseline justify-between text-xs text-muted-foreground">
                      <span>Subtotal</span>
                      <Importe monto={subtotal} moneda={moneda} tamano="sm" />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="shrink-0 text-xs text-muted-foreground">Descuento</span>
                      {tipoDto === "MONTO" ? (
                        // Un descuento en plata es un campo de plata, con la
                        // moneda del ticket impuesta. Si no parsea emite `null`
                        // y el descuento anterior se conserva: nunca un 0 a
                        // escondidas.
                        <InputImporte
                          valor={descuentoValor > 0 ? descuentoValor : null}
                          moneda={moneda}
                          onValorChange={(v) => {
                            if (v !== null && v >= 0) onCambiarDescuento("MONTO", v);
                          }}
                          className="w-32"
                        />
                      ) : (
                        // Vaciar el campo es un 0 pedido; un texto que no parsea
                        // NO lo es: se marca y el descuento anterior se conserva.
                        <Input
                          value={descuentoTexto}
                          onChange={(e) => {
                            const crudo = e.target.value;
                            setDescuentoTexto(crudo);
                            if (crudo.trim() === "") {
                              onCambiarDescuento("PORCENTAJE", 0);
                              return;
                            }
                            const n = parsearImporte(crudo);
                            if (n !== null && n >= 0) onCambiarDescuento("PORCENTAJE", n);
                          }}
                          inputMode="decimal"
                          placeholder="0"
                          aria-label="Descuento sobre el total"
                          aria-invalid={descuentoInvalido}
                          className="w-20 text-right tabular-nums"
                        />
                      )}
                      {/* % o monto es una eleccion real: "10" es ambiguo entre diez
                      por ciento y diez pesos. */}
                      <Segmented
                        opciones={[
                          { valor: "PORCENTAJE" as const, etiqueta: "%" },
                          { valor: "MONTO" as const, etiqueta: moneda === "ARS" ? "$" : "US$" },
                        ]}
                        valor={tipoDto}
                        onChange={(t) => onCambiarDescuento(t, descuentoValor)}
                        etiqueta="Tipo de descuento"
                        apariencia="segmentado"
                        tamano="sm"
                      />
                      <Importe
                        monto={descuento === 0 ? 0 : -descuento}
                        moneda={moneda}
                        tamano="sm"
                        className="ml-auto shrink-0"
                      />
                    </div>
                  </div>
                )}

                {/* `flex-wrap`: en el celular el total no entra al lado de
                    «Hacer descuento» y lo tapaba. Ahora baja a su linea. */}
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="text-sm text-muted-foreground">Total</span>
                    {descuento === 0 && !mostrarDto && (
                      <Button variant="outline" size="sm" onClick={() => setMostrarDto(true)}>
                        <Tag aria-hidden="true" />
                        Hacer descuento
                      </Button>
                    )}
                  </div>
                  <div className="ml-auto flex shrink-0 flex-col items-end">
                    {/* SIEMPRE en pesos, sin importar en que moneda se facture: es
                    la moneda con la que se cobra casi todo. */}
                    <Importe monto={totalArs} moneda="ARS" tamano="xl" className="font-semibold" />

                    {/* Debajo, de donde sale cada parte. Un total unico esconde
                    cuanto de la venta esta atado al dolar, que es justo la
                    parte que cambia de precio mañana. */}
                    <div className="mt-0.5 flex flex-wrap items-center justify-end gap-1 text-xs text-muted-foreground">
                      {mixto ? (
                        <>
                          <Importe monto={desglose.USD} moneda="USD" tamano="sm" />
                          <span>+</span>
                          <Importe monto={desglose.ARS} moneda="ARS" tamano="sm" />
                        </>
                      ) : desglose.USD > 0 ? (
                        <Importe monto={desglose.USD} moneda="USD" tamano="sm" />
                      ) : (
                        <Importe
                          monto={convertir(totalArs, "ARS", "USD", cotizacion)}
                          moneda="USD"
                          tamano="sm"
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* El comprobante tiene UNA moneda —no puede tener dos adentro, no
                se podria imputar ni cobrar—, asi que lo que esta en la otra se
                convierte y ese precio queda congelado. Tiene que verse. */}
                {convertidos.length > 0 && (
                  <Aviso tono="neutral" icono={ArrowLeftRight} compacto className="mt-2">
                    Se factura en {moneda === "ARS" ? "pesos" : "dólares"}, así que{" "}
                    <span className="font-medium">
                      {convertidos.length === 1
                        ? convertidos[0]!.nombre
                        : `${convertidos.length} productos`}
                    </span>{" "}
                    se {convertidos.length === 1 ? "convirtió" : "convirtieron"} al dólar de{" "}
                    <span className="font-medium tabular-nums">
                      {formatearCotizacion(cotizacion)}
                    </span>
                    .
                  </Aviso>
                )}
              </div>

              <div className={cn(paso === "ticket" && "max-md:hidden")}>
                {/* Paso 2 en el celular: volver al ticket, y el total a la vista. */}
                <div className="flex items-center justify-between gap-2 px-4 pt-3 md:hidden">
                  <Button variant="ghost" size="sm" onClick={() => setPaso("ticket")}>
                    <ChevronLeft aria-hidden="true" />
                    Ticket · {items.length} {items.length === 1 ? "producto" : "productos"}
                  </Button>
                  <Importe monto={totalArs} moneda="ARS" tamano="lg" className="font-semibold" />
                </div>

                {/* Contado o cuenta corriente. Solo con cliente: sin cliente no hay
              cuenta a la cual cargar la deuda. */}
                {clienteId !== null && (
                  <div className="px-4 pt-3">
                    <Segmented
                      opciones={[
                        { valor: "AHORA" as const, etiqueta: "Cobrar ahora" },
                        { valor: "CUENTA" as const, etiqueta: "A la cuenta" },
                      ]}
                      valor={aCuenta ? "CUENTA" : "AHORA"}
                      onChange={(v) => onCambiarACuenta(v === "CUENTA")}
                      etiqueta="Forma de cobro"
                      apariencia="segmentado"
                      tamano="sm"
                      className="flex w-full [&>button]:flex-1"
                    />
                  </div>
                )}

                {!aCuenta && (
                  <div className="space-y-2 px-4 py-3">
                    {/* El saldo a favor del cliente, ofrecido y nunca aplicado
                      solo: quien vende elige si lo usa y cuanto. Va antes de
                      los pagos para que lo que se proponga cobrar sea el resto. */}
                    {topeSaldo > 0.009 && (
                      <Card variant="muted" padding="compacto" className="space-y-2">
                        <Checkbox
                          checked={saldoElegido !== null}
                          onChange={(e) => setSaldoElegido(e.target.checked ? topeSaldo : null)}
                          label={
                            <>
                              Usar su saldo a favor:{" "}
                              <Importe monto={saldoDisponible} moneda={moneda} tamano="sm" />
                            </>
                          }
                          ayuda="No sale de la caja ni se devuelve en plata: paga esta venta."
                        />
                        {saldoElegido !== null && (
                          <InputImporte
                            label="¿Cuánto usa?"
                            valor={saldoUsado}
                            moneda={moneda}
                            sinNegativos
                            ayuda={`Hasta ${formatear(topeSaldo, moneda)}.`}
                            onValorChange={(v) => {
                              if (v !== null && v >= 0) setSaldoElegido(Math.min(v, topeSaldo));
                            }}
                          />
                        )}
                      </Card>
                    )}

                    {/* El mismo editor que el cobro de cuenta corriente, la seña y
                      la entrega. Cada fila arranca con LO QUE FALTA, no con
                      cero: es el numero que la persona iba a tipear igual. */}
                    <EditorPagos
                      pagos={pagos}
                      onChange={setPagos}
                      cuentas={cuentas}
                      objetivo={objetivoPlata(toma)}
                      monedaObjetivo={moneda}
                      cotizacion={cotizacion}
                      permitirVacio
                      etiquetaVacio="¿Cómo paga?"
                    />

                    {/* El equipo que deja en parte de pago. No es plata: no entra
                  a ninguna caja, entra al stock — pero acá vale como pago. */}
                    {entrega === null ? (
                      <Button variant="dashed" size="sm" onClick={() => setDialogEntrega(true)}>
                        <Smartphone aria-hidden="true" />
                        Deja un equipo en parte de pago
                      </Button>
                    ) : (
                      <Card variant="muted" padding="compacto" className="flex items-center gap-2">
                        <Smartphone
                          className="size-4 shrink-0 text-muted-foreground"
                          aria-hidden="true"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs font-medium">{entrega.nombre}</p>
                          <p className="text-xs text-muted-foreground">
                            Toma:{" "}
                            <Importe
                              monto={entrega.valor_toma}
                              moneda={entrega.moneda ?? moneda}
                              tamano="sm"
                            />
                          </p>
                        </div>
                        <IconButton
                          icono={X}
                          tamano="sm"
                          onClick={() => setEntrega(null)}
                          aria-label="Quitar el equipo en parte de pago"
                          className="shrink-0 text-muted-foreground hover:text-destructive"
                        />
                      </Card>
                    )}

                    {(falta || vuelto) &&
                      (pagos.length > 0 || entrega !== null || saldoUsado > 0.009) && (
                        <Aviso
                          tono={falta || vueltoSinCajon ? "warning" : "success"}
                          // Sin cliente la deuda no tiene donde vivir. En vez de un
                          // error que frena, se ofrece la salida ahi mismo: el mismo
                          // buscador permite elegir uno existente o darlo de alta.
                          accion={
                            parcialSinCliente ? (
                              <Button variant="outline" size="sm" onClick={() => onElegirCliente()}>
                                <UserPlus aria-hidden="true" />
                                Asignar o crear un cliente
                              </Button>
                            ) : undefined
                          }
                        >
                          <div className="flex items-center justify-between gap-2 font-medium">
                            <span>
                              {vuelto
                                ? ofrecePropina && vueltoPropina
                                  ? "Propina"
                                  : "Vuelto"
                                : parcial
                                  ? "Queda debiendo"
                                  : "Falta cobrar"}
                            </span>
                            <Importe monto={Math.abs(diferencia)} moneda={moneda} />
                          </div>
                          {ofrecePropina && (
                            <Checkbox
                              className="mt-2"
                              checked={vueltoPropina}
                              onChange={(e) => setVueltoPropina(e.target.checked)}
                              label="Lo deja de propina"
                              ayuda="El vuelto no sale: queda en la caja."
                            />
                          )}
                          {vuelto && !(ofrecePropina && vueltoPropina) && cajaVuelto && (
                            <p className="mt-1 text-xs">
                              Sale de {nombreCaja(cajaVuelto)}
                              {cajaVuelto.moneda !== moneda && (
                                <>
                                  {": "}
                                  {/* Los pesos que da el cajero, contados sobre lo
                                    que entro: convertir la diferencia en dolares
                                    arrastraba el redondeo (US$ 2,58 daba $ 4.102,20
                                    en vez de $ 4.100). */}
                                  <Importe
                                    monto={vueltoEnPesos({
                                      pagos,
                                      cuentas,
                                      entrega,
                                      saldoUsado,
                                      total,
                                      moneda,
                                      cotizacion,
                                    })}
                                    moneda={cajaVuelto.moneda}
                                    tamano="sm"
                                  />
                                </>
                              )}
                              .
                            </p>
                          )}
                          {vueltoSinCajon && (
                            <p className="mt-1 text-xs">
                              No hay una caja de Efectivo en pesos activa para dar el vuelto: el
                              vuelto siempre sale en pesos. Activala en Caja o cobrá justo.
                            </p>
                          )}
                          {parcialSinCliente && (
                            <p className="mt-2 text-xs">
                              Una deuda necesita un cliente para saber a quién cobrársela.
                            </p>
                          )}
                        </Aviso>
                      )}
                  </div>
                )}

                {/* Lo a pedido se avisa antes de cobrar, con o sin cliente: la
                  venta sale, pero el equipo no se entrega hoy. */}
                {hayAPedido && (
                  <div className="px-4 pb-3">
                    <Aviso
                      tono={encargoSinCliente ? "warning" : "info"}
                      compacto
                      accion={
                        encargoSinCliente ? (
                          <Button variant="outline" size="sm" onClick={() => onElegirCliente()}>
                            <UserPlus aria-hidden="true" />
                            Asignar o crear un cliente
                          </Button>
                        ) : undefined
                      }
                    >
                      {encargoSinCliente
                        ? "Lo que es a pedido queda como encargo, y un encargo necesita un cliente para avisarle cuando llegue."
                        : "Lo que es a pedido queda como encargo: se entrega cuando llegue."}
                    </Aviso>
                  </div>
                )}

                {chequeSinCliente && (
                  <div className="px-4 pb-3">
                    <Aviso
                      tono="warning"
                      compacto
                      accion={
                        <Button variant="outline" size="sm" onClick={() => onElegirCliente()}>
                          <UserPlus aria-hidden="true" />
                          Asignar o crear un cliente
                        </Button>
                      }
                    >
                      Para cobrar con cheque hace falta el cliente: si el cheque rebota, queda
                      debiéndolo.
                    </Aviso>
                  </div>
                )}

                {totalCero && (
                  <div className="px-4 pb-3">
                    <Aviso tono="warning" compacto>
                      El total es {formatear(0, moneda)}: revisá el precio o el descuento.
                    </Aviso>
                  </div>
                )}

                {aCuenta && (
                  <div className="px-4 py-3 text-xs text-muted-foreground">
                    Queda como deuda de {clienteNombre}. Se cobra después desde su cuenta corriente.
                  </div>
                )}

                {deudaEnSuCuenta !== null && clienteAnclaje !== null && (
                  <div className="px-4 pb-3">
                    <Aviso tono="warning" compacto>
                      {clienteAnclaje === "ARS" ? (
                        <>
                          Su cuenta es en pesos: esta deuda queda fija en{" "}
                          <Importe monto={deudaEnSuCuenta} moneda="ARS" tamano="sm" />, aunque suba
                          el dólar.
                        </>
                      ) : (
                        <>
                          Su cuenta está atada al dólar: esta deuda queda en{" "}
                          <Importe monto={deudaEnSuCuenta} moneda="USD" tamano="sm" />.
                        </>
                      )}
                    </Aviso>
                  </div>
                )}

                {/* Sin "Vaciar" aca: hacia exactamente lo mismo que "Reiniciar"
                  del encabezado, que ahora esta siempre visible. Dos botones
                  para la misma accion, uno de ellos al lado de Cobrar, es una
                  invitacion a equivocarse.

                  La banda es sticky abajo para que en el celular, con diez
                  items, Cobrar no quede detras del scroll. Sin sombra: no es
                  un overlay. La zona segura del iPhone la suma el contenedor
                  de afuera (`area-segura-abajo`), el `py-3` va adentro. */}
                <div className="area-segura-abajo sticky bottom-0 border-t border-border bg-card">
                  <div className="px-4 py-3 sm:flex sm:justify-end">
                    {/* La etiqueta nombra la plata que se mueve, y no cambia
                      mientras registra: `cargando` pone el spinner y bloquea.
                      Es un string y no un <Importe>: sobre el primario negro
                      el color de moneda no se lee. */}
                    <Button
                      className="w-full sm:w-auto"
                      onClick={cobrar}
                      disabled={!puedeCobrar}
                      cargando={guardando}
                    >
                      {etiquetaCobrar({
                        aCuenta,
                        parcial: parcial && !parcialSinCliente,
                        conEquipo: entrega !== null,
                        plata,
                        cobrado,
                        diferencia,
                        total,
                        moneda,
                        saldoAFavor: saldoUsado,
                      })}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/*
        Paso 1 en el celular: fijo abajo, el total y el botón para ir a cobrar.
        Va al final de la tarjeta y no adentro del bloque del total: ahí el
        `sticky` no podía salir de ese bloque, tapaba el total con 5 productos
        y con 6 quedaba fuera de la pantalla.
      */}
      {!sinDestinatario && items.length > 0 && (
        <div
          className={cn(
            "area-segura-abajo sticky bottom-0 z-10 border-t border-border bg-card md:hidden",
            paso === "cobro" && "hidden",
          )}
        >
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="flex shrink-0 flex-col">
              <span className="text-xs text-muted-foreground">Total</span>
              <Importe monto={totalArs} moneda="ARS" tamano="lg" className="font-semibold" />
            </div>
            <div className="min-w-0 flex-1">
              <Button className="w-full" onClick={irACobrar} disabled={totalCero}>
                Cómo paga
                <ChevronRight aria-hidden="true" />
              </Button>
            </div>
          </div>
        </div>
      )}

      <DialogUsado
        abierto={dialogEntrega}
        onAbiertoChange={setDialogEntrega}
        modo="entrega"
        // El valor de toma arranca en la moneda de la venta: en una venta en
        // US$ arrancaba en pesos, y «1.115» pensando en dolares cargaba una
        // toma de $ 1.115.
        monedaInicial={moneda}
        cotizacion={cotizacion}
        onGuardar={(form) => {
          const nueva = aEntrega(form);
          // Si ya habia una sola fila con lo que el sistema propuso, se vuelve
          // a proponer con el equipo descontado: quedaba en el total de antes
          // y anunciaba de vuelto lo que vale el equipo. Una fila escrita a
          // mano no se toca.
          const unico = pagos.length === 1 ? pagos[0] : undefined;
          const caja = unico ? cuentas.find((c) => c.id === unico.cuenta_id) : undefined;
          if (
            unico &&
            caja &&
            unico.importe !== null &&
            Math.abs(
              proponerImporte(objetivoPlata(toma), [], cuentas, moneda, caja, cotizacion) -
                unico.importe,
            ) < 0.005
          ) {
            const tomaNueva = convertir(
              nueva.valor_toma,
              nueva.moneda ?? moneda,
              moneda,
              cotizacion,
            );
            setPagos([
              {
                ...unico,
                importe: proponerImporte(
                  objetivoPlata(tomaNueva),
                  [],
                  cuentas,
                  moneda,
                  caja,
                  cotizacion,
                ),
              },
            ]);
          }
          setEntrega(nueva);
          setDialogEntrega(false);
        }}
      />
    </div>
  );
}
